import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  Audience,
  AudienceContact,
  ReasonDistribution,
  UnsubscribedContact,
} from '@/types/newsletter'

interface AudienceListResponse {
  data: Audience[]
  pagination: { page: number; per_page: number; total: number }
}

interface AudienceContactsResponse {
  data: AudienceContact[]
  pagination: { page: number; per_page: number; total: number }
}

interface CreateAudienceRequest {
  name: string
  description?: string
  unsub_message?: string | null
  unsub_reasons?: string[] | null
}

interface UpdateAudienceRequest {
  name?: string
  description?: string
  unsub_message?: string | null
  unsub_reasons?: string[] | null
}

interface AddContactsRequest {
  audience_id: string
  contacts: Array<{ email: string; metadata?: Record<string, unknown> }>
}

interface AddContactsResponse {
  added: number
  updated: number
  skipped: number
  skipped_emails: string[]
}

interface SyncContactsRequest {
  audience_id: string
  contacts: Array<{ email: string; metadata?: Record<string, unknown> }>
}

interface SyncContactsResponse {
  added: number
  updated: number
  cleaned: number
}

interface ContactListParams {
  page?: number
  per_page?: number
  search?: string
  status?: string
}

const QUERY_KEY = ['audiences'] as const
const CONTACTS_KEY = ['audience-contacts'] as const

export function useAudiences(params?: {
  page?: number
  per_page?: number
}): ReturnType<typeof useQuery<AudienceListResponse>> {
  return useQuery({
    queryKey: [...QUERY_KEY, params],
    queryFn: () => apiClient.get<AudienceListResponse>(endpoints.audiences.list(params)),
  })
}

export function useAudience(id: string): ReturnType<typeof useQuery<Audience>> {
  return useQuery({
    queryKey: [...QUERY_KEY, id],
    queryFn: () => apiClient.get<Audience>(endpoints.audiences.detail(id)),
    enabled: !!id,
  })
}

export function useCreateAudience(): ReturnType<
  typeof useMutation<Audience, unknown, CreateAudienceRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateAudienceRequest) =>
      apiClient.post<Audience>(endpoints.audiences.create, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useUpdateAudience(): ReturnType<
  typeof useMutation<Audience, unknown, { id: string; data: UpdateAudienceRequest }>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAudienceRequest }) =>
      apiClient.put<Audience>(endpoints.audiences.update(id), data),
    onSuccess: (_result, { id }) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      qc.invalidateQueries({ queryKey: [...QUERY_KEY, id] })
    },
  })
}

export function useDeleteAudience(): ReturnType<typeof useMutation<unknown, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(endpoints.audiences.delete(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useAudienceContacts(
  id: string,
  params?: ContactListParams,
): ReturnType<typeof useQuery<AudienceContactsResponse>> {
  return useQuery({
    queryKey: [...CONTACTS_KEY, id, params],
    queryFn: () =>
      apiClient.get<AudienceContactsResponse>(endpoints.audiences.contacts(id, params)),
    enabled: !!id,
  })
}

export function useAddContacts(): ReturnType<
  typeof useMutation<AddContactsResponse, unknown, AddContactsRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ audience_id, contacts }: AddContactsRequest) =>
      apiClient.post<AddContactsResponse>(endpoints.audiences.addContacts(audience_id), {
        contacts,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      qc.invalidateQueries({ queryKey: CONTACTS_KEY })
    },
  })
}

export function useRemoveContacts(): ReturnType<
  typeof useMutation<unknown, unknown, { audience_id: string; emails: string[] }>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ audience_id, emails }: { audience_id: string; emails: string[] }) =>
      apiClient.post<unknown>(endpoints.audiences.removeContacts(audience_id), {
        emails,
        _method: 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      qc.invalidateQueries({ queryKey: CONTACTS_KEY })
    },
  })
}

export function useSyncContacts(): ReturnType<
  typeof useMutation<SyncContactsResponse, unknown, SyncContactsRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ audience_id, contacts }: SyncContactsRequest) =>
      apiClient.post<SyncContactsResponse>(endpoints.audiences.sync(audience_id), { contacts }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      qc.invalidateQueries({ queryKey: CONTACTS_KEY })
    },
  })
}

interface UnsubscribedContactsResponse {
  data: UnsubscribedContact[]
  pagination: { page: number; per_page: number; total: number }
  reason_distribution: ReasonDistribution[]
}

const UNSUB_CONTACTS_KEY = ['audience-unsub-contacts'] as const

export function useUnsubscribedContacts(
  audienceId: string,
  params?: { page?: number; per_page?: number },
): ReturnType<typeof useQuery<UnsubscribedContactsResponse>> {
  return useQuery({
    queryKey: [...UNSUB_CONTACTS_KEY, audienceId, params],
    queryFn: () =>
      apiClient.get<UnsubscribedContactsResponse>(
        endpoints.audiences.contacts(audienceId, { ...params, status: 'unsubscribed' }),
      ),
    enabled: !!audienceId,
  })
}

export type {
  AddContactsRequest,
  AddContactsResponse,
  AudienceContactsResponse,
  AudienceListResponse,
  ContactListParams,
  CreateAudienceRequest,
  SyncContactsRequest,
  SyncContactsResponse,
  UpdateAudienceRequest,
}
